// Monitor fáze — Current Fit vs Original Fit všech kandidátů focusu.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowsClockwise, TrendUp, TrendDown, Warning } from "@phosphor-icons/react";
import { monitorRefresh, CANDIDATE_STATUS_LABELS } from "../../lib/lab-api";

export default function FocusMonitor({ focus, candidates, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const items = candidates.filter((c) => c.status !== "REJECTED")
        .sort((a, b) => (b.current_fit_score ?? b.original_fit_score ?? 0) - (a.current_fit_score ?? a.original_fit_score ?? 0));
    const mainCid = focus.decision?.main?.cid;

    const refresh = async () => {
        setBusy(true);
        try {
            const d = await monitorRefresh(focus.id);
            setAlerts(d.alerts || []);
            toast.success(d.alerts?.length ? `Přepočteno — ${d.alerts.length} upozornění` : "Přepočteno — žádné významné změny");
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Přepočet selhal"); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-3" data-testid="focus-monitor">
            <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={refresh} disabled={busy} data-testid="monitor-refresh"
                        className="font-mono text-[9px] uppercase tracking-widest px-3 py-1.5 border border-fa-mint text-fa-mint hover:bg-fa-mint hover:text-black font-bold flex items-center gap-1.5 disabled:opacity-40">
                    <ArrowsClockwise size={11} className={busy ? "animate-spin" : ""} /> Přepočítat Current Fit
                </button>
                <span className="font-mono text-[9px] text-white/40">Original Fit se nikdy nepřepisuje — porovnáváš plán vs. realitu. Upozornění jdou do zvonečku.</span>
            </div>
            {alerts.map((a, i) => (
                <p key={i} className="font-mono text-[10px] text-[#FF8800] border border-[#FF8800]/30 bg-[#FF8800]/5 px-3 py-2 flex items-center gap-2" data-testid={`monitor-alert-${i}`}>
                    <Warning size={12} weight="fill" /> {a}
                </p>
            ))}
            <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                        <tr className="font-mono text-[8px] uppercase tracking-widest text-white/40 border-b border-white/10">
                            <th className="px-3 py-2">Kandidát</th>
                            <th className="px-3 py-2">Status</th>
                            <th className="px-3 py-2 text-right">Original Fit</th>
                            <th className="px-3 py-2 text-right">Current Fit</th>
                            <th className="px-3 py-2 text-right">Δ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((c) => {
                            const isMain = c.id === mainCid;
                            const d = c.fit_delta;
                            return (
                                <tr key={c.id} className={`border-b border-white/5 ${isMain ? "bg-fa-mint/5" : ""}`} data-testid={`monitor-row-${c.fpl_id}`}>
                                    <td className="px-3 py-1.5">
                                        <Link to={`/players/${c.fpl_id}`} className="font-display font-bold text-[12px] hover:text-fa-mint">{c.name}</Link>
                                        {isMain && <span className="font-mono text-[7px] uppercase px-1 py-0.5 border border-fa-mint/60 text-fa-mint ml-1.5">Volba</span>}
                                        {c.is_my_tip && <span className="text-fa-yellow ml-1">★</span>}
                                        <div className="font-mono text-[8px] text-white/40">{c.team} · £{c.price}{c.preseason_share != null && <span className="text-fa-sky"> · přípr. {c.preseason_share} %</span>}</div>
                                    </td>
                                    <td className="px-3 py-1.5 font-mono text-[9px] text-white/55">{CANDIDATE_STATUS_LABELS[c.status]}</td>
                                    <td className="px-3 py-1.5 font-mono text-[11px] tabular-nums text-right text-white/70">{c.original_fit_score != null ? Math.round(c.original_fit_score) : "—"}</td>
                                    <td className="px-3 py-1.5 font-mono text-[11px] tabular-nums text-right font-bold">{c.current_fit_score != null ? Math.round(c.current_fit_score) : "—"}</td>
                                    <td className="px-3 py-1.5 text-right">
                                        {d != null && d !== 0 ? (
                                            <span className={`font-mono text-[10px] tabular-nums inline-flex items-center gap-0.5 ${d > 0 ? "text-fa-mint" : "text-red-300"}`}>
                                                {d > 0 ? <TrendUp size={10} /> : <TrendDown size={10} />}{d > 0 ? "+" : ""}{d}
                                            </span>
                                        ) : <span className="font-mono text-[10px] text-white/30">—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
